import { useQuery } from '@tanstack/react-query';
import { Store, ShoppingCart, DollarSign, Package } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

/**
 * Mirrors the `/admin/marketplace-analytics` handler
 * (functions/src/routes/admin/marketplace-analytics.ts → GET /). The real
 * `totals` keys are `total_orders` / `total_revenue` / `total_items_sold`
 * (NOT the bare orders/revenue/items_sold names). `::int` columns come back
 * as numbers; `::numeric` columns as strings.
 */
interface MarketplaceTotals {
  total_orders: number;
  total_revenue: number | string;
  total_fees: number | string;
  total_items_sold: number;
  total_clicks: number;
  total_impressions: number;
}

interface MarketplaceChannel {
  channel_id: string;
  channel_name: string;
  platform: string;
  total_orders: number;
  total_revenue: number | string;
  total_fees: number | string;
  total_clicks: number;
  total_impressions: number;
  total_items_sold: number;
}

interface MarketplaceAnalyticsResponse {
  period: { days: number; since: string };
  totals: MarketplaceTotals;
  channels: MarketplaceChannel[];
  daily: unknown[];
}

export default function MarketplaceSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'marketplace', 'analytics'],
    queryFn: () => api.get<MarketplaceAnalyticsResponse>('/admin/marketplace-analytics'),
    staleTime: 60000,
  });

  return (
    <WidgetShell
      title="Marketplace"
      icon={<Store />}
      to="/admin/marketplace/analytics"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && data.channels.length === 0}
      emptyIcon={<Store />}
      emptyMessage="No marketplace channels connected yet."
      skeletonClassName="h-40"
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Orders"
            value={Number(data.totals.total_orders).toLocaleString()}
            icon={<ShoppingCart />}
            tone="blue"
          />
          <StatCard
            title="Revenue"
            value={formatCurrency(Number(data.totals.total_revenue))}
            icon={<DollarSign />}
            tone="green"
          />
          <StatCard
            title="Channels"
            value={data.channels.length}
            icon={<Store />}
            tone="purple"
          />
          <StatCard
            title="Items sold"
            value={Number(data.totals.total_items_sold).toLocaleString()}
            icon={<Package />}
            tone="teal"
          />
        </div>
      )}
    </WidgetShell>
  );
}
